import autobind from 'core-decorators/lib/autobind';
import ReactTransitionEvents from 'react/lib/ReactTransitionEvents';

import { VODCSSModules } from 'shared/utils/wix-connect';
import { getWidth, getSlideWidth, getTrackLeft } from './sizes';

import CarouselButton from './buttons';
import Track from './track';

import styles from './mobile-carousel.styl';

/*
 Mobile carousel is inspired by https://github.com/akiran/react-slick/tree/master/src
*/


function getTrackCSS({ slideCount, slidesToShow, slideWidth, left }) {
  const trackWidth = (slideCount + 2 * slidesToShow) * slideWidth;

  return {
    opacity: 1,
    width: trackWidth,
    WebkitTransform: `translate3d(${left}px, 0px, 0px)`,
    transform: `translate3d(${left}px, 0px, 0px)`,
    transition: '',
    WebkitTransition: '',
    msTransform: `translateX(${left}px)`,
  };
}

function getTrackAnimateCSS(spec) {
  const style = getTrackCSS(spec);
  // useCSS is true by default so it can be undefined
  style.WebkitTransition = `-webkit-transform '${spec.speed}ms ${spec.cssEase}`;
  style.transition = `transform ${spec.speed}ms ${spec.cssEase}`;
  return style;
}

function calcSwipeDirection(touchObject) {
  const xDist = touchObject.startX - touchObject.curX;
  const yDist = touchObject.startY - touchObject.curY;
  const r = Math.atan2(yDist, xDist);

  let swipeAngle = Math.round(r * 180 / Math.PI);
  if (swipeAngle < 0) {
    swipeAngle = 360 - Math.abs(swipeAngle);
  }
  if ((swipeAngle <= 45) && (swipeAngle >= 0) || (swipeAngle <= 360) && (swipeAngle >= 315)) {
    return 'left';
  }
  if ((swipeAngle >= 135) && (swipeAngle <= 225)) {
    return 'right';
  }

  return 'vertical';
}


@VODCSSModules(styles)
export default class MobileCarousel extends React.Component {

  static propTypes = {
    totalCount: React.PropTypes.number,
    slidesToShow: React.PropTypes.number,
    afterChange: React.PropTypes.func,
    children: React.PropTypes.any,
    className: React.PropTypes.string,
    touchThreshold: React.PropTypes.number,
    slidesToScroll: React.PropTypes.number,
    initialSlide: React.PropTypes.number,
    slidesPreviewWidth: React.PropTypes.number,
    cssEase: React.PropTypes.string,
    edgeFriction: React.PropTypes.number,
    speed: React.PropTypes.number,
    getHeight: React.PropTypes.func,
    beforeChange: React.PropTypes.func,
    waitForAnimate: React.PropTypes.bool,
    showButtons: React.PropTypes.bool,
    trackClassName: React.PropTypes.string,
    onUserStartedTracking: React.PropTypes.func,
  };

  static defaultProps = {
    className: '',
    cssEase: 'ease',
    easing: 'linear',
    edgeFriction: 0.35,
    initialSlide: 0,
    slidesToScroll: 1,
    speed: 500,
    getHeight: _.constant(200),
    slidesToShow: 3,
    touchThreshold: 5,
    useCSS: true,
    waitForAnimate: true,
    afterChange: _.noop,
    beforeChange: _.noop,
    onUserStartedTracking: _.noop,
    slidesPreviewWidth: 0,
    showButtons: true,
  };

  constructor(props) {
    super(props);

    this._currentSlide = props.initialSlide;

    this.state = {
      slideWidth: null,
    };
  }

  componentDidMount() {
    this.update();
    window.addEventListener('resize', this.update);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.update);
  }

  _touchObject = {
    startX: 0,
    startY: 0,
    curX: 0,
    curY: 0,
  };

  @autobind
  update() {
    if (!this.wrapper) {
      return;
    }

    const { slidesToShow, slidesPreviewWidth } = this.props;
    const listWidth = getWidth(this.list);
    const trackWidth = getWidth(this.track);
    const slideWidth = getSlideWidth(this.wrapper, slidesToShow, { slidesPreviewWidth });

    this.setState({
      slideWidth,
      listWidth,
      trackWidth,
    }, () => {
      const targetLeft = this.getTrackLeft(this._currentSlide, slideWidth, slidesPreviewWidth);
      // getCSS function needs previously set state
      const trackStyle = getTrackCSS(_.assign({
        left: targetLeft,
        slideCount: this.getSlidesCount(),
      }, this.props, this.state));

      this.setTrackStyle(trackStyle);
    });
  }

  getTrackLeft(slideIndex, slideWidth, slidesPreviewWidth) {
    return getTrackLeft(this.getSlidesCount(), this.props.slidesToShow, slideIndex, slideWidth, slidesPreviewWidth);
  }

  setTrackStyle(style) {
    _.assign(this.track.style, style);
  }

  @autobind
  swipeStart(e) {
    const posX = _.isUndefined(e.touches) ? e.clientX : e.touches[0].pageX;
    const posY = _.isUndefined(e.touches) ? e.clientY : e.touches[0].pageY;

    this._dragging = true;
    this._animating = false;
    this._swipeStarted = false;

    this._touchObject = {
      startX: posX,
      startY: posY,
      curX: posX,
      curY: posY,
    };
  }

  @autobind
  getSlidesCount() {
    return React.Children.count(this.props.children);
  }

  @autobind
  swipeMove(e) {
    if (!this._dragging) {
      return;
    }
    if (this._animating) {
      return;
    }

    const touchObject = _.clone(this._touchObject);
    const { slideWidth } = this.state;
    const { slidesToShow, slidesPreviewWidth, onUserStartedTracking } = this.props;
    const currentSlide = this._currentSlide;

    const curLeft = this.getTrackLeft(currentSlide, slideWidth, slidesPreviewWidth);
    touchObject.curX = (e.touches) ? e.touches[0].pageX : e.clientX;
    touchObject.curY = (e.touches) ? e.touches[0].pageY : e.clientY;
    touchObject.swipeLength = Math.round(Math.sqrt(Math.pow(touchObject.curX - touchObject.startX, 2)));

    const positionOffset = touchObject.curX > touchObject.startX ? 1 : -1;

    const slidesCount = this.getSlidesCount();
    const swipeDirection = calcSwipeDirection.call(this, touchObject);
    let touchSwipeLength = touchObject.swipeLength;

    if ((currentSlide === 0 && swipeDirection === 'right') || ((currentSlide >= slidesCount - slidesToShow) && swipeDirection === 'left')) {
      touchSwipeLength = touchObject.swipeLength * this.props.edgeFriction;
    }

    const swipeLeft = curLeft + touchSwipeLength * positionOffset;
    this._touchObject = touchObject;

    const trackStyle = getTrackCSS(_.assign({
      left: swipeLeft,
      slideCount: this.getSlidesCount(),
    }, this.props, this.state));

    this.setTrackStyle(trackStyle);

    if (this._swipeStarted) {
      // prevent vertical scrolling in browsers what doesn't support passive touch events
      // https://developers.google.com/web/updates/2016/06/passive-event-listeners
      e.preventDefault();
      return;
    }

    if (touchObject.swipeLength > 4) {
      this._swipeStarted = true;
      onUserStartedTracking();
    }
  }

  calcSlidesToScroll(swipeLength) {
    const { slideWidth, listWidth } = this.state;
    const { touchThreshold } = this.props;
    const minSwipe = listWidth / touchThreshold;
    let slidesCount = Math.floor(swipeLength / slideWidth);
    const lastSlideLength = swipeLength % slideWidth;
    if (lastSlideLength > minSwipe) {
      slidesCount += 1;
    }
    return slidesCount;
  }

  @autobind
  swipeEnd(e) {
    if (!this._dragging) {
      return;
    }
    const touchObject = this._touchObject;
    const minSwipe = this.state.listWidth / this.props.touchThreshold;
    const swipeDirection = calcSwipeDirection.call(this, touchObject);
    const currentSlide = this._currentSlide;

    // reset the state of touch related state variables.
    this._dragging = false;
    this._touchObject = {};
    // Fix for #13
    if (!touchObject.swipeLength) {
      return;
    }
    if (touchObject.swipeLength > minSwipe) {
      const slidesToScroll = this.calcSlidesToScroll(touchObject.swipeLength);
      e.preventDefault();
      if (swipeDirection === 'left') {
        this.slideHandler(currentSlide + slidesToScroll);
      } else if (swipeDirection === 'right') {
        this.slideHandler(currentSlide - slidesToScroll);
      } else {
        this.slideHandler(currentSlide);
      }
    } else {
      // Adjust the track back to it's original position.
      const currentLeft = this.getTrackLeft(
        currentSlide, this.state.slideWidth, this.props.slidesPreviewWidth
      );

      this.setTrackStyle(
        getTrackAnimateCSS(_.assign({
          left: currentLeft,
          slideCount: this.getSlidesCount(),
        }, this.props, this.state)));
    }
  }

  slideHandler(index) {
    // Functionality of animateSlide and postSlide is merged into this function

    if (this.props.waitForAnimate && this._animating) {
      return;
    }

    let currentSlide;
    const slideCount = this.getSlidesCount();
    const targetSlide = index;

    if (targetSlide < 0) {
      currentSlide = 0;
    } else if (targetSlide > slideCount - this.props.slidesToShow) {
      currentSlide = slideCount - this.props.slidesToShow;
    } else {
      currentSlide = targetSlide;
    }

    const targetLeft = this.getTrackLeft(currentSlide, this.state.slideWidth, this.props.slidesPreviewWidth);

    if (this.props.beforeChange) {
      this.props.beforeChange(this._currentSlide, currentSlide);
    }

    // Slide Transition happens here.
    // animated transition happens to target Slide and
    // non - animated transition happens to current Slide

    const nextStateChanges = {
      trackStyle: getTrackCSS({
        left: targetLeft,
        slideCount: this.getSlidesCount(),
        ...this.props,
        ...this.state,
      }),
      swipeLeft: null,
    };

    const callback = () => {
      this._animating = false;
      this.setTrackStyle(nextStateChanges.trackStyle);
      this.props.afterChange(currentSlide);
      ReactTransitionEvents.removeEndEventListener(this.track, callback);
    };

    this._animating = true;
    const trackStyle = getTrackAnimateCSS(_.assign({
      left: targetLeft,
      slideCount: this.getSlidesCount(),
    }, this.props, this.state));
    this.setTrackStyle(trackStyle);

    this._currentSlide = currentSlide;
    ReactTransitionEvents.addEndEventListener(this.track, callback);
  }

  changeSlide({ action, slideIndex }) {
    let slideOffset;
    let targetSlide;
    const slideCount = this.getSlidesCount();
    const currentSlide = this._currentSlide;
    const slidesToScroll = this.props.slidesToScroll;
    const unevenOffset = (slideCount % slidesToScroll !== 0);
    const indexOffset = unevenOffset ? 0 : (slideCount - currentSlide) % slidesToScroll;

    if (action === 'previous') {
      slideOffset = (indexOffset === 0) ? slidesToScroll : this.props.slidesToShow - indexOffset;
      targetSlide = currentSlide - slideOffset;
    } else if (action === 'next') {
      slideOffset = (indexOffset === 0) ? slidesToScroll : indexOffset;
      targetSlide = currentSlide + slideOffset;
    } else if (action === 'index') {
      targetSlide = slideIndex;
      if (targetSlide === currentSlide) {
        return;
      }
    }

    this.slideHandler(targetSlide);
  }

  @autobind
  handlePrevButtonClick() {
    this.changeSlide({ action: 'previous' });
  }

  @autobind
  handleNextButtonClick() {
    this.changeSlide({ action: 'next' });
  }

  get buttons() {
    const { showButtons, slidesToShow } = this.props;
    const currentSlide = this._currentSlide;

    if (!showButtons) {
      return null;
    }

    const slidesCount = this.getSlidesCount();

    return [
      (
        <CarouselButton key="prev"
                        direction="left"
                        onClick={this.handlePrevButtonClick}
                        isActive={currentSlide > 0} />
      ),
      (
        <CarouselButton key="next"
                        direction="right"
                        onClick={this.handleNextButtonClick}
                        isActive={currentSlide < slidesCount - slidesToShow} />
      ),
    ];
  }

  @autobind
  renderChildren() {
    const { slideWidth } = this.state;
    const { getHeight, children } = this.props;

    if (!slideWidth) {
      return null;
    }

    return React.Children.map(children, elem =>
      React.cloneElement(
        elem, {
          width: slideWidth,
          height: getHeight(elem, slideWidth),
        }
      )
    );
  }

  @autobind
  getTrackRef(node) {
    this.track = node;
  }

  @autobind
  setWrapper(node) {
    this.wrapper = node;
    this.update();
  }

  render() {
    const trackProps = {
      cssEase: this.props.cssEase,
      speed: this.props.speed,
      currentSlide: this._currentSlide,
      slideWidth: this.state.slideWidth,
      slidesToShow: this.props.slidesToShow,
      slideCount: this.getSlidesCount(),
      trackStyle: this.state.trackStyle,
    };

    const { className, trackClassName } = this.props;

    return (
      <div ref={this.setWrapper}
           styleName="container"
           className={className}>
        <div ref={node => (this.list = node)}
             className="slick-list"
             onMouseDown={this.swipeStart}
             onMouseMove={this.swipeMove}
             onMouseUp={this.swipeEnd}
             onMouseLeave={this.swipeEnd}
             onTouchStart={this.swipeStart}
             onTouchMove={this.swipeMove}
             onTouchEnd={this.swipeEnd}
             onTouchCancel={this.swipeEnd}>
          <Track getRef={this.getTrackRef}
                 className={trackClassName}
                 {...trackProps}>
            {this.renderChildren()}
          </Track>
        </div>
        {this.buttons}
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/widget/components/mobile-carousel/mobile-carousel.js